// Hook centralizado pra ler as linhas editoriais do cliente ativo.
//
// Antes essas linhas estavam HARDCODED em 5 páginas (Pautas, Pecas, Insights,
// BancoIdeias, LinhaEditorial). Agora vêm do banco — uma config por cliente,
// editável via Admin.
//
// Uso:
//   const { lines, byCode, isLoading } = useEditorialLines();
//   lines.map(l => <Badge color={l.color}>{l.codigo} {l.nome}</Badge>)
//
// Inclui fallback estável (cores fixas L1..L6) pra quando lines vier vazia
// (cliente novo ainda não seedado).

import { useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";

export interface EditorialLine {
  id: number;
  clientId: number;
  ordem: number;
  codigo: string;
  nome: string;
  color: string;
  bg: string;
  descricao: string | null;
  objetivo: string | null;
  frequenciaPct: number;
  tom: string | null;
  pilares: string[];
  formatos: string[];
  personas: string[];
  exemplos: string[];
  mixSemanal: string | null;
  ativo: boolean;
}

// Cores default por ordem L1..L6 — se o cliente ainda não configurou cor.
const DEFAULT_COLORS: { color: string; bg: string }[] = [
  { color: "#0A0A0A", bg: "#EDEBE6" },
  { color: "#2563EB", bg: "#DBEAFE" },
  { color: "#10B981", bg: "#D1FAE5" },
  { color: "#52525B", bg: "#FEF3C7" },
  { color: "#8B5CF6", bg: "#EDE9FE" },
  { color: "#EC4899", bg: "#FDE8F5" },
];

export function useEditorialLines(clientIdOverride?: number) {
  const { session } = useAuth();
  const clientId = clientIdOverride ?? session?.activeClientId ?? session?.clientId ?? 1;

  const query = trpc.clients.getEditorialLines.useQuery(
    { clientId },
    { enabled: !!clientId, staleTime: 5 * 60_000 }
  );

  const lines: EditorialLine[] = useMemo(() => {
    const raw = (query.data ?? []) as EditorialLine[];
    // Garante cor default se algum campo veio vazio
    return raw.map((l, i) => ({
      ...l,
      color: l.color || DEFAULT_COLORS[i % 6].color,
      bg: l.bg || DEFAULT_COLORS[i % 6].bg,
    }));
  }, [query.data]);

  const byCode = useMemo(() => {
    const m = new Map<string, EditorialLine>();
    for (const l of lines) m.set(l.codigo, l);
    return m;
  }, [lines]);

  // Lookup defensivo: usado pelos cards/badges em Pautas/Insights/etc
  // Aceita "L1 — Compliance & Controle" ou "L1" ou só prefixo
  function getColorByValue(value: string | null | undefined): { color: string; bg: string } {
    if (!value) return { color: "#262626", bg: "#F5F4F1" };
    // Tenta achar pelo código (L1, L2…) — primeiro token
    const prefix = value.split(/[\s—-]/)[0]?.trim();
    if (prefix && byCode.has(prefix)) {
      const l = byCode.get(prefix)!;
      return { color: l.color, bg: l.bg };
    }
    // Tenta match exato pelo nome
    for (const l of lines) {
      if (value.startsWith(`${l.codigo} ${l.nome}`) || value === `${l.codigo} — ${l.nome}`) {
        return { color: l.color, bg: l.bg };
      }
    }
    return { color: "#262626", bg: "#F5F4F1" };
  }

  // Lista pra <select> ou chips — formato "L1 — Nome"
  const optionValues = useMemo(
    () => lines.map(l => `${l.codigo} — ${l.nome}`),
    [lines]
  );

  return {
    lines,
    byCode,
    optionValues,
    getColorByValue,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
